import React, { useRef, useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { colors, book, heavy, regular, semiBold } from '@/styles';
import { ProgressCircle } from '@/atoms';
import { VideoStatus } from '@/constants';
import { ImagePlaceholder } from '@/svgs';

/**
 * VideoThumbnail component
 *
 * @param {import('./VideoThumbnail').VideoThumbnailProps} props
 */
function VideoThumbnail({ progress, status, className, thumbnail, width, isRebrand }) {
  const containerRef = useRef(null);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [showError, setShowError] = useState('');
  const [emptyHeight, setEmptyHeight] = useState(0);
  const [thumbnailState, setThumbnailState] = useState({
    radius: 60,
    displayProgress: false,
  });

  const handleImageError = useCallback(() => {
    if (!containerRef.current) {
      return;
    }
    setShowPlaceholder(true);
    setShowError(true);
  }, [containerRef]);

  const handleImageLoad = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    setShowPlaceholder(false);
    setThumbnailState({
      radius: containerRef.current.clientHeight * 0.5,
      displayProgress: true,
    });
  }, [containerRef]);

  useEffect(() => {
    setShowPlaceholder(!thumbnail);
  }, [thumbnail]);

  useEffect(() => {
    if (containerRef.current) {
      setEmptyHeight(containerRef.current.clientWidth * (9 / 16));
    }
  }, [containerRef]);

  useEffect(() => {
    if ([VideoStatus.UPLOAD_FAILED, VideoStatus.ENCODING_FAILED].includes(status)) {
      setShowError('Failed');
      setShowPlaceholder(false);
      setThumbnailState({ displayProgress: false });
    } else if (status === VideoStatus.READY && !thumbnail) {
      setShowPlaceholder(true);
    } else {
      setThumbnailState({
        displayProgress: true,
      });
      setShowPlaceholder(false);
    }
  }, [status, thumbnail]);

  const progressCircleColor = showError ? colors.recordRed : colors.taosSky;

  return (
    <div className={className} ref={containerRef}>
      {thumbnail || showPlaceholder ? (
        <picture
          className={classnames({ placeholder: showPlaceholder, darken: !!showError })}
          style={{ height: `${emptyHeight}px` }}
        >
          {thumbnail && !showPlaceholder ? (
            <img
              alt=""
              src={thumbnail}
              onLoad={() => handleImageLoad()}
              onError={() => handleImageError()}
              draggable="false"
            />
          ) : (
            <ImagePlaceholder className="placeholder--video-thumbnail" />
          )}
        </picture>
      ) : (
        <div className={classnames('message', { error: !!showError })} style={{ height: '100%' }}>
          {showError ?? '&nbsp;'}
        </div>
      )}

      {status !== VideoStatus.READY && thumbnailState.displayProgress && (
        <div className={classnames('overlay', { darken: !!showError })} title={showError}>
          <ProgressCircle
            progress={!showError ? progress : 100}
            size={thumbnailState.radius}
            textColor={progressCircleColor}
            strokeColor={progressCircleColor}
            strokeModifier={0.05}
            showBackground
          />
        </div>
      )}
    </div>
  );
}

VideoThumbnail.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** What status is the thumbnail in */
  status: PropTypes.oneOf([
    'UPLOADING',
    'UPLOAD_FAILED',
    'ENCODING',
    'ENCODING_FAILED',
    'PROCESSING',
    'READY',
  ]).isRequired,
  /** 1-100 % for uploading, encoding, and processing state */
  progress: PropTypes.number,
  /** thumbnail image used for ready state */
  thumbnail: PropTypes.string,
  /** width attribute */
  width: PropTypes.string,
  isRebrand: PropTypes.bool,
};

VideoThumbnail.defaultProps = {
  progress: 0,
  thumbnail: '',
  width: '100%',
  isRebrand: false,
};

const styledVideoThumbnail = styled(VideoThumbnail)`
  display: grid;
  grid-template-columns: [one] 1fr;
  grid-template-rows: [one] 1fr;
  justify-items: center;
  align-items: center;
  position: relative;
  width: 100%;
  padding: 0;
  margin: 0;
  background-color: ${colors.snow};
  ${(props) => {
    if (props.width.includes('%')) {
      return `height: ${props.width};`;
    }

    if (props.width.includes('px')) {
      const width = parseInt(props.width.replace('px', ''), 10);
      const ratioed = width * 0.5625;
      return `height: ${ratioed}px`;
    }
    return 'height: 100%;';
  }};

  .overlay {
    grid-area: one;
    font-size: 0.937rem;
    width: 80%;
    height: 80%;

    &.darken {
      background-color: rgba(0, 0, 0, 0.5);
      color: ${colors.white};
    }
  }

  .message {
    grid-area: one;
    &.error {
      ${({ isRebrand }) => (isRebrand ? semiBold() : heavy())}
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: ${10 / 16}rem;
      color: ${colors.recordRed};
    }
  }

  img,
  .uploading {
    max-width: 100%;
    max-height: 100%;

    svg text {
      ${({ isRebrand }) => (isRebrand ? regular() : book())}
      font-size: ${40 / 16}rem;
    }
  }

  picture {
    svg {
      display: none;
    }

    &.darken polygon,
    &.darken path {
      fill: #aaa;
    }

    &.placeholder {
      display: flex;
      align-items: center;
      align-content: center;
      justify-content: center;

      img {
        display: none;
      }

      svg {
        display: inline;
        width: 25%;
      }
    }
  }
`;

export default styledVideoThumbnail;
